import { useEffect, useRef, useState, useCallback } from "react";

const RECONNECT_BASE = 2000;
const RECONNECT_MAX = 30000;
const PING_INTERVAL = 25000;
const POLL_INTERVAL = 10000; // fallback polling every 10s

export default function useRealtimeSync(onSync) {
  const [status, setStatus] = useState("connecting"); // connecting | connected | polling | disconnected
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const pollRef = useRef(null);
  const retriesRef = useRef(0);
  const wsFailedRef = useRef(false);
  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;

  // Fallback polling
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    setStatus("polling");
    pollRef.current = setInterval(() => {
      onSyncRef.current("poll");
    }, POLL_INTERVAL);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      startPolling();
      return;
    }

    const wsUrl = backendUrl.replace(/^http/, "ws") + "/api/ws";
    setStatus("connecting");

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      const connectTimeout = setTimeout(() => {
        // If still connecting after 5s, give up and poll
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      }, 5000);

      ws.onopen = () => {
        clearTimeout(connectTimeout);
        setStatus("connected");
        stopPolling();
        retriesRef.current = 0;
        wsFailedRef.current = false;
        // Start ping keepalive
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "sync") {
            onSyncRef.current(data.action);
          }
        } catch {
          // ignore
        }
      };

      ws.onclose = () => {
        clearTimeout(connectTimeout);
        cleanup();
        retriesRef.current++;

        // After 3 failed attempts, switch to polling permanently
        if (retriesRef.current >= 3) {
          wsFailedRef.current = true;
          startPolling();
          return;
        }

        // Exponential backoff reconnect
        const delay = Math.min(RECONNECT_BASE * Math.pow(2, retriesRef.current), RECONNECT_MAX);
        startPolling(); // poll while waiting to reconnect
        reconnectRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        clearTimeout(connectTimeout);
        ws.close();
      };
    } catch {
      // WebSocket constructor failed
      startPolling();
    }
  }, [startPolling, stopPolling]);

  function cleanup() {
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
  }

  useEffect(() => {
    connect();
    return () => {
      cleanup();
      stopPolling();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect, stopPolling]);

  return status;
}
